import { hasAuthParams } from "@bcmi-labs/art-auth"
import { noop } from "@tanstack/react-query"
import { createRootRouteWithContext, HeadContent, Outlet, redirect, Scripts } from "@tanstack/react-router"
import { memo } from "react"

import { query } from "@/api/query"
import { policiesQuery } from "@/api/query/policies"
import { MY_SPACE } from "@/constants/spaces"
import { type RouterContext } from "@/router"

/*
 * ==================
 * Application structure
 * ==================
 */
function App() {
  return (
    <div style={{ display: "flex", flex: 1 }}>
      <HeadContent />
      <Outlet />
      <Scripts />
    </div>
  )
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: memo(App),
  pendingMs: 200, // Relax the pending state to avoid content flashing
  beforeLoad: async ({ cause, context: { authClient, queryClient, spaceStorage } }) => {
    // Try prefilling token cache
    await authClient.checkSession()

    // If we're seeing auth params in the URL, we're likely coming from an external auth provider
    if (hasAuthParams() && !(await authClient.isAuthenticated())) {
      const appState = new URLSearchParams((await authClient.handleRedirectCallback()).appState)

      // Remove pathname from the query string
      const pathname = appState.get("pathname") || "/"
      if (pathname) appState.delete("pathname")

      // Get all other query params insto a key-value object
      const search = Object.fromEntries(appState)

      redirect({
        to: pathname,
        search,
        throw: true
      })
    }

    // RBAC: on app entry, prefetch every organization's permission matrix once, waiting for the current space's one so
    // gated UI renders already resolved. A failure grants no permissions; it's retried only when switching to that space (see app.tsx).
    // TODO: Make space changes refresh the whole roles/permissions state together (space list roles, recap limits, policies),
    // instead of only retrying failed policies.
    if (cause === "enter") {
      const spaceId = spaceStorage.getSpace()
      const spaceList = await queryClient.query({ ...query.space.list, staleTime: "static" })
      const organizations = spaceList.filter(space => space.id !== MY_SPACE.id)

      if (organizations.some(space => space.id === spaceId)) {
        await queryClient.query(policiesQuery(spaceId)).catch(noop)
      }
      // Prefetch the other organizations only afterwards, so they never compete with the current space's request.
      for (const space of organizations) {
        if (space.id !== spaceId) void queryClient.query(policiesQuery(space.id)).catch(noop)
      }
    }
  },
  head: () => ({ meta: [{ title: "Arduino Cloud" }] })
})
